// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OwnProps } from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@west/App/Store/Selectors/SettingsSelectors";
import { StyledBox } from "@west/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    currentCurrencySymbol: getSession(state).currency?.currencySymbol,
    shippingPromotionThreshold: getSettingsCollection(state).customSettings.shippingPromotionThreshold,
});

export interface ShippingPromotionStyles {
    container?: InjectableCss;
    message?: TypographyPresentationProps;
    stepperContainer?: InjectableCss;
    stepper?: InjectableCss;
    innerStepper?: InjectableCss;
    priceLabel?: TypographyPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const shippingPromotionStyles: ShippingPromotionStyles = {};

const styles = shippingPromotionStyles;
export const ShippingPromotion = ({ cart, currentCurrencySymbol, shippingPromotionThreshold }: Props) => {
    if (
        !cart ||
        !shippingPromotionThreshold ||
        cart.orderSubTotal === undefined ||
        cart.orderSubTotal > shippingPromotionThreshold
    ) {
        return null;
    }

    const diff = Number((shippingPromotionThreshold - cart.orderSubTotal).toFixed(2));
    const percent = 100 - (diff / shippingPromotionThreshold) * 100;
    return (
        <StyledBox {...styles.container}>
            <Typography {...styles.message}>
                {translate("You’re just")}
                <Typography weight="bold">
                    {currentCurrencySymbol}
                    {diff}
                </Typography>
                {translate("away from FREE shipping!")}
            </Typography>
            <StyledWrapper {...styles.stepperContainer}>
                <Typography {...styles.priceLabel}>{translate(`${currentCurrencySymbol}0`)}</Typography>
                <StyledWrapper {...styles.stepper}>
                    <StyledWrapper {...{ percent }} {...styles.innerStepper}></StyledWrapper>
                </StyledWrapper>
                <Typography
                    {...styles.priceLabel}
                >{`${currentCurrencySymbol}${shippingPromotionThreshold}`}</Typography>
            </StyledWrapper>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ShippingPromotion),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        displayName: "Shipping Promotion",
    },
};

export default widgetModule;
